import {useCallback, useState} from 'react';
import {useRouteLoaderData} from 'react-router-dom';
import serverRoutesConst from '@/constants/serverRoutesConst';
import {getUserSession} from '@/helpers/userHelper';

export const downloadErrors = {
  noDocuments: 'noDocuments',
  generic: 'generic',
};

const REVOKE_DELAY_MS = 60000;

// The backend is authoritative for the filename (it carries the candidate initials + assessment id).
// CorsFilter exposes Content-Disposition; if an intermediary strips it, fall back to a plain name.
function getFileName(response, assessmentId) {
  const disposition = response.headers.get('Content-Disposition') || '';
  const match = disposition.match(/filename="?([^";]+)"?/i);
  return match?.[1]?.trim() || `documentos-${assessmentId}.zip`;
}

const fetchDocuments = (assessmentId, token) =>
  fetch(`${serverRoutesConst.API}/assessments/${assessmentId}/documents/download`, {
    method: 'GET',
    headers: {Authorization: `Bearer ${token}`},
  });

function useDownloadAssessmentDocuments() {
  const {userSession} = useRouteLoaderData('root') || {};
  const [isDownloading, setIsDownloading] = useState(false);
  const [error, setError] = useState(null);

  const reset = useCallback(() => setError(null), []);

  const download = useCallback(
    async (assessmentId) => {
      const token = userSession?.accessToken?.jwtToken;
      if (!assessmentId || !token) {
        setError(downloadErrors.generic);
        return;
      }

      setIsDownloading(true);
      setError(null);
      try {
        let response = await fetchDocuments(assessmentId, token);

        // The token snapshot from the loader can be stale on a long-lived logbook
        // tab; refresh once and retry, mirroring useSaveLogbook's onErrorRetry.
        if (response.status === 401) {
          const newSession = await getUserSession();
          const newToken = newSession?.accessToken?.jwtToken;
          if (newToken) {
            response = await fetchDocuments(assessmentId, newToken);
          }
        }

        if (response.status === 204) {
          setError(downloadErrors.noDocuments);
          return;
        }
        if (!response.ok) {
          setError(downloadErrors.generic);
          return;
        }

        const blob = await response.blob();
        const objectUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = objectUrl;
        link.download = getFileName(response, assessmentId);
        document.body.appendChild(link);
        link.click();
        link.remove();
        // Defer the revoke: revoking synchronously can cancel the download of a large
        // archive before the browser has finished reading the blob.
        setTimeout(() => URL.revokeObjectURL(objectUrl), REVOKE_DELAY_MS);
      } catch {
        setError(downloadErrors.generic);
      } finally {
        setIsDownloading(false);
      }
    },
    [userSession]
  );

  return {download, isDownloading, error, reset};
}

export default useDownloadAssessmentDocuments;
